import { webAPIUrl } from './AppSettings';
import { accountService } from './account.service';

export interface HttpRequest<REQB> {
    path: string;
    method?: string;
    body?: REQB;
    accessToken?: string;
    file?: File;
    authHeader?: any;
    formData?: boolean;
    skipAuth?: boolean; // skip JWT bearer header (e.g. cross-origin POST to SecureWeb)
    timeoutMs?: number; // abort after this many ms; on timeout, message === 'Timeout'
}

export interface HttpResponse<RESB> {
    ok: boolean;
    body?: RESB;
    message?: string;
    // Full parsed JSON body for non-OK responses. Lets callers read structured
    // error payloads (e.g. the 409 duplicate-order body's status /
    // existingOrderId / completedAt) instead of only the flattened message.
    error?: HttpErrorBody;
}

// Shape of a structured error body returned by the API on a non-OK response.
// The fields beyond `message` are populated only by endpoints that return a
// structured payload — today the create-order duplicate guard (HTTP 409).
export interface HttpErrorBody {
    message?: string;
    status?: string; // e.g. "duplicate_recent_order"
    existingOrderId?: string;
    completedAt?: string;
    retryAfterMinutes?: number;
    [key: string]: unknown;
}

function getBody(config) {
    if (config.file) {
        return config.file;
    } else if (config.formData) {
        const formData = new FormData();
        for (const key in config.body) {
            formData.append(String(key), String(config.body[key]));
        }
        return formData;
    } else if (config.body) {
        return JSON.stringify(config.body);
    } else {
        return undefined;
    }
}

function getContentType(config): string | null {
    if (config.formData) {
        return null;
    }
    if (config.file) {
        return 'multipart/form-data';
    } else {
        return 'application/json';
    }
}

export const http = async <RESB, REQB = undefined>(config: HttpRequest<REQB>): Promise<HttpResponse<RESB>> => {
    const headers = {};
    const contentType = getContentType(config);
    if (contentType) {
        headers['Content-Type'] = contentType;
    }

    const controller = new AbortController();
    const timeoutId = config.timeoutMs ? setTimeout(() => controller.abort(), config.timeoutMs) : null;

    const request = new Request(config.path.startsWith('http') ? config.path : `${webAPIUrl}${config.path}`, {
        method: config.method || 'GET',
        headers: {
            ...headers,
            ...config.authHeader,
        },
        body: getBody(config),
        signal: controller.signal,
    });

    if (!config.skipAuth) {
        var account = accountService.getAccount();
        if (account) {
            request.headers.set('authorization', `bearer ${account.jwtToken}`);
        }
    }

    try {
        const response = await fetch(request);
        if (response.ok) {
            if (response.status === 204) {
                return { ok: response.ok };
            } else {
                const body = await response.json();
                return { ok: response.ok, body };
            }
        } else {
            if (response.status === 401) {
                accountService.logout();
                // Preserve the existing generic message for most callers, but
                // expose a structured marker so checkout screens can show
                // session-expired guidance instead of a payment failure.
                return {
                    ok: false,
                    message: 'Unauthorized',
                    error: { status: 'unauthorized' },
                };
            }
            if (response.status === 403) {
                return { ok: false, message: 'Too many requests. Please try again later.' };
            }
            const body: HttpErrorBody = await response.json();
            console.error(`Error requesting ${request.method} ${request.url}`, body?.message);
            // Carry the full parsed body through (error) so structured payloads
            // like the 409 duplicate body survive, while keeping the flattened
            // message for callers that only look at result.message.
            return { ok: response.ok, message: body?.message, error: body };
        }
    } catch (e: any) {
        if (e?.name === 'AbortError') {
            return { ok: false, message: 'Timeout' };
        }
        return { ok: false, message: 'Connection error' };
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
    }
};

// const logError = async (request: Request, response: Response) => {
//     const contentType = response.headers.get('content-type');
//     let body: any;
//     if (contentType && contentType.indexOf('application/json') !== -1) {
//         body = await response.json();
//     } else {
//         body = await response.text();
//     }
//     console.error(`Error requesting ${request.method} ${request.url}`, body);
// };
